import React, { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { AlertTriangle, BarChart2, ChevronDown, Info, MoreHorizontal } from 'lucide-react'
import TaskLogForm, { ComplianceTimeline, type LotRow } from '@/features/task-logging/TaskLogForm'
import { buildLotValidityWarnings } from '@/features/task-logging/buildLotValidityWarnings'
import type { LoggerOption } from '@/features/task-logging/TaskLogForm'
import {
  TaskStatusBadge,
  taskStatusRowTint,
  taskStatusRowTintClassName,
} from '@/features/task-status'
import { TaskNotesPanel } from '@/features/task-notes/TaskNotesPanel'
import { ControlsReagentsLotsModal } from '@/features/instrument-controls-reagents/ControlsReagentsLotsModal'
import LogDetailsDialog from '../ReportsTab/LogDetailsDialog'
import { reloadInstrumentAndScheduleAfterVoid } from '../ReportsTab/calendarLogDeleteRefresh'
import { DataFieldChartDialog, hasChartableDataFieldType } from '../shared/DataFieldChartDialog'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { FrequencyContextPanel } from '@/shared/ui/frequency-context-panel'
import { approximateIntervalHoursFromRrule } from '@/shared/lib/frequency/rrule-utils'
import { cn } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'
import { supplyDisplayName } from '@/shared/lib/supply-label'
import {
  historyInstrumentTaskTaskLogPath,
  instrumentTaskDataFieldsPath,
  instrumentTaskTaskLogPath,
} from '@/shared/lib/routes'
import type WebInstrumentsCurrentScheduleTask from '@/shared/types/web/WebInstrumentsCurrentScheduleTask'
import type { WebInstrumentsLotIndex } from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

type CurrentScheduleProp = {
  tasks: WebInstrumentsCurrentScheduleTask[]
  canTest: boolean
  instrumentStatusLabel: string | null
  loggerOptions: LoggerOption[]
  logAuthenticationCode: string
  siteTimeZone?: string | null
}

interface InstrumentTasksAccordionProps {
  instrumentId: number
  instrumentLabel: string
  currentSchedule: CurrentScheduleProp
  lots: WebInstrumentsLotIndex[]
  errors?: Record<string, string | string[]>
  canOpenTaskLog: boolean
  canConfigureTaskDataFields: boolean
  canPersistLotSelection: boolean
  canRetireLot: boolean
  canSubmitTaskLog: boolean
  logAuthenticationCode?: string | null
  /** Called after a task log is submitted or voided so sibling panels (e.g. the Reports
   *  tab calendar) can refresh without a full page reload. */
  onAfterTaskLog?: () => void
}

const SORT_OPTIONS = ['status', 'default', 'frequency', 'alphabetical', 'tag'] as const
type SortOption = (typeof SORT_OPTIONS)[number]

type TaskSectionKey =
  | 'overdue'
  | 'dueNow'
  | 'ready'
  | 'upToDate'
  | 'unscheduled'
  | 'scheduledTasks'
  | 'unscheduledTasks'
  | 'flat'

type TaskSection = {
  key: TaskSectionKey
  /** i18n key under instruments.show.tasks.sortBy.sections — omit for flat sorts. */
  labelKey?: Exclude<TaskSectionKey, 'flat'>
  tasks: WebInstrumentsCurrentScheduleTask[]
}

const STATUS_SECTION_CODES: Array<{ code: string; key: Exclude<TaskSectionKey, 'flat'> }> = [
  { code: 'ODE', key: 'overdue' },
  { code: 'DNW', key: 'dueNow' },
  { code: 'RDY', key: 'ready' },
  { code: 'UTD', key: 'upToDate' },
]

/** Match Postgres ORDER BY name (byte order), not localeCompare — Name / Tag sort modes only. */
const compareNameSql = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

// Planning / Default / Calendar parity: task_order ASC NULLS LAST, then id.
// Legacy never sorted by name for planning order (collation diverges UAT glibc vs local musl).
// Null taskOrder sorts last so legacy rows do not jump ahead of ordered tasks.
const byTaskOrderThenId = (
  a: WebInstrumentsCurrentScheduleTask,
  b: WebInstrumentsCurrentScheduleTask,
) =>
  (a.taskOrder ?? Number.POSITIVE_INFINITY) - (b.taskOrder ?? Number.POSITIVE_INFINITY) ||
  a.id - b.id

const byName = (a: WebInstrumentsCurrentScheduleTask, b: WebInstrumentsCurrentScheduleTask) =>
  compareNameSql(a.name ?? '', b.name ?? '')

// Null tag names sort last so untagged tasks fall to the bottom of the tag view.
const compareNullableString = (a: string | null, b: string | null) => {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return compareNameSql(a, b)
}

const firstTagName = (task: WebInstrumentsCurrentScheduleTask): string | null =>
  task.tags[0]?.name ?? null

const isUnscheduledTask = (task: WebInstrumentsCurrentScheduleTask) =>
  task.frequencyTypeCode === 'UNSCHEDULED'

// Prefer stored interval_hours; for Calendar Fixed (often null hours) derive from RRULE.
// Unknown cadence sorts last within Scheduled (not as 0 — that floated Yearly to the top).
const intervalHoursForSort = (task: WebInstrumentsCurrentScheduleTask) => {
  const storedHours = task.complianceWindow?.intervalHours
  if (storedHours != null && storedHours > 0) return storedHours

  const rruleHours = approximateIntervalHoursFromRrule(task.frequencyRrule)
  if (rruleHours != null) return rruleHours

  return Number.POSITIVE_INFINITY
}

const byIntervalHoursThenTaskOrder = (
  a: WebInstrumentsCurrentScheduleTask,
  b: WebInstrumentsCurrentScheduleTask,
) => intervalHoursForSort(a) - intervalHoursForSort(b) || byTaskOrderThenId(a, b)

interface CannotTestAlertProps {
  instrumentStatusLabel: string | null
}

export const CannotTestAlert = ({ instrumentStatusLabel }: CannotTestAlertProps) => (
  <div
    className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    role="alert"
    data-testid="cannot-test-alert"
  >
    {instrumentStatusLabel
      ? t('instruments.show.tasks.cannotTestAlert', { status: instrumentStatusLabel })
      : t('instruments.show.tasks.cannotTestAlertNoStatus')}
  </div>
)

export const FailedNeedsRerunRibbon = () => (
  <span
    className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-1 text-sm text-destructive"
    role="status"
    data-testid="failed-needs-rerun-ribbon"
  >
    <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
    <strong className="font-semibold">{t('instruments.show.tasks.failedNeedsRerun.label')}</strong>
    <span className="font-normal">{t('instruments.show.tasks.failedNeedsRerun.hint')}</span>
  </span>
)

export function buildTaskSections(
  tasks: WebInstrumentsCurrentScheduleTask[],
  sortOption: SortOption,
): TaskSection[] {
  const list = [...tasks]

  switch (sortOption) {
    case 'default':
      return [{ key: 'flat', tasks: list.sort(byTaskOrderThenId) }]
    case 'alphabetical':
      return [{ key: 'flat', tasks: list.sort(byName) }]
    case 'tag':
      return [
        {
          key: 'flat',
          tasks: list.sort(
            (a, b) => compareNullableString(firstTagName(a), firstTagName(b)) || byName(a, b),
          ),
        },
      ]
    case 'frequency': {
      const scheduled = list
        .filter((task) => !isUnscheduledTask(task))
        .sort(byIntervalHoursThenTaskOrder)
      const unscheduled = list.filter(isUnscheduledTask).sort(byTaskOrderThenId)
      const sections: TaskSection[] = [
        { key: 'scheduledTasks', labelKey: 'scheduledTasks', tasks: scheduled },
        { key: 'unscheduledTasks', labelKey: 'unscheduledTasks', tasks: unscheduled },
      ]
      return sections.filter((section) => section.tasks.length > 0)
    }
    case 'status': {
      const scheduled = list.filter((task) => !isUnscheduledTask(task))
      const unscheduled = list.filter(isUnscheduledTask).sort(byTaskOrderThenId)
      const sections: TaskSection[] = STATUS_SECTION_CODES.map(({ code, key }) => ({
        key,
        labelKey: key,
        tasks: scheduled.filter((task) => task.taskStatusCode === code).sort(byTaskOrderThenId),
      }))
      const knownStatusCodes = new Set(STATUS_SECTION_CODES.map((section) => section.code))
      // Tasks with an unrecognised status code fall here — no label, appear at bottom (AC 3).
      sections.push({
        key: 'flat',
        tasks: scheduled
          .filter(
            (task) => task.taskStatusCode == null || !knownStatusCodes.has(task.taskStatusCode),
          )
          .sort(byTaskOrderThenId),
      })
      sections.push({
        key: 'unscheduledTasks',
        labelKey: 'unscheduledTasks',
        tasks: unscheduled,
      })
      return sections.filter((section) => section.tasks.length > 0)
    }
    default:
      return [{ key: 'flat', tasks: list.sort(byTaskOrderThenId) }]
  }
}

function lotValidityWarningsForTask(task: WebInstrumentsCurrentScheduleTask) {
  const linkedLotForWarnings: LotRow | null =
    task.linkedLot == null
      ? null
      : ({
          id: task.linkedLot.id,
          name: task.linkedLot.name,
          lotNumber: task.linkedLot.lotNumber,
          expirationDate: task.linkedLot.expirationDate,
          currentLotDetailId: task.currentLotDetailId,
          lifecycleStatus: task.linkedLot.lifecycleStatus,
          isLotRetired: task.linkedLot.isLotRetired,
          isLotDiscarded: task.linkedLot.isLotDiscarded,
        } as LotRow)

  const usedLotRows: LotRow[] = (task.usedLots ?? []).map(
    (usedLot) =>
      ({
        id: usedLot.id,
        name: usedLot.name,
        lotNumber: usedLot.lotNumber,
        expirationDate: usedLot.expirationDate,
        currentLotDetailId: usedLot.currentLotDetailId,
        lifecycleStatus: usedLot.lifecycleStatus,
        isLotRetired: usedLot.isLotRetired,
        isLotDiscarded: usedLot.isLotDiscarded,
      }) as LotRow,
  )

  return buildLotValidityWarnings({
    linkedLot: linkedLotForWarnings,
    usedLots: usedLotRows,
    dataFields: task.dataFields,
  })
}

function hasChartableFields(task: WebInstrumentsCurrentScheduleTask): boolean {
  return task.dataFields.some((dataField) => hasChartableDataFieldType(dataField.dataFieldType))
}

// ── InstrumentTasksAccordion ──────────────────────────────────────────────────

const InstrumentTasksAccordion = ({
  instrumentId,
  instrumentLabel,
  currentSchedule,
  lots,
  errors,
  canOpenTaskLog,
  canConfigureTaskDataFields,
  canPersistLotSelection,
  canRetireLot,
  canSubmitTaskLog,
  logAuthenticationCode,
  onAfterTaskLog,
}: InstrumentTasksAccordionProps) => {
  const { formatDateTime } = useSiteDateTime()
  const { taskHistoryEnabled } = usePage<SharedProps>().props
  const [openTaskId, setOpenTaskId] = useState<number | null>(null)
  const [detailsTaskLogId, setDetailsTaskLogId] = useState<{
    taskId: number
    logId: number
  } | null>(null)
  const [chartTaskId, setChartTaskId] = useState<number | null>(null)
  const [sortOption, setSortOption] = useState<SortOption>('default')

  const { tasks, canTest, instrumentStatusLabel, loggerOptions, siteTimeZone } = currentSchedule

  const toggleTask = (taskId: number) => {
    setOpenTaskId((prev) => (prev === taskId ? null : taskId))
  }

  const handleInlineSuccess = () => {
    setOpenTaskId(null)
    onAfterTaskLog?.()
  }

  // Tasks-tab UX gate (E27-S31 / AC9): when canTest is false show only the block alert.
  // Legacy hides the accordion via *ngIf="canTest"; rebuild also hides Sort By (alert-only).
  // Standalone Log create and dashboard queue are intentionally not gated here.
  if (!canTest) {
    return <CannotTestAlert instrumentStatusLabel={instrumentStatusLabel} />
  }

  if (tasks.length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        {t('instruments.show.tasks.noScheduledTasks')}
      </p>
    )
  }

  // canTest already gated by the early return above; remaining gate is permission only.
  const canLogInline = canSubmitTaskLog
  const taskSections = buildTaskSections(tasks, sortOption)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {t('instruments.show.tasks.sortBy.label')}
        </span>
        <div className="flex flex-wrap gap-1 rounded-md bg-muted p-1">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSortOption(option)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-all',
                sortOption === option
                  ? 'bg-primary text-primary-foreground shadow'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm',
              )}
              aria-pressed={sortOption === option}
            >
              {t(`instruments.show.tasks.sortBy.${option}` as const)}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border divide-y">
        {taskSections.map((section) => (
          <React.Fragment key={section.key}>
            {section.labelKey && (
              <div className="bg-muted/40 px-3 py-2 text-sm font-semibold text-foreground">
                {t(`instruments.show.tasks.sortBy.sections.${section.labelKey}` as const)}
              </div>
            )}
            {section.tasks.map((task) => {
              const isOpen = openTaskId === task.id
              // A discarded lot has been removed from the instrument type — it must never be
              // selectable at task level (REBUILD-306), so drop it before building any row/modal.
              const hasLinkedLot = task.linkedLot != null && !task.linkedLot.isLotDiscarded
              const usedLotsList = (task.usedLots ?? []).filter(
                (usedLot) => !usedLot.isLotDiscarded,
              )
              const hasAnyLot = hasLinkedLot || usedLotsList.length > 0
              const showActions =
                canOpenTaskLog ||
                canConfigureTaskDataFields ||
                (hasAnyLot && canPersistLotSelection)

              const lotValidityWarnings = lotValidityWarningsForTask(task)

              // Build per-lot modal slots for linked + all used lots.
              const lotModalSlots = canPersistLotSelection
                ? (() => {
                    const slots = new Map<number, React.ReactNode>()
                    const allLotIds = [
                      ...(hasLinkedLot ? [task.linkedLot!.id] : []),
                      ...usedLotsList.map((ul) => ul.id),
                    ]
                    for (const lotId of allLotIds) {
                      const currentDetailId =
                        lotId === task.linkedLot?.id
                          ? task.currentLotDetailId
                          : (usedLotsList.find((ul) => ul.id === lotId)?.currentLotDetailId ?? null)
                      slots.set(
                        lotId,
                        <ControlsReagentsLotsModal
                          key={lotId}
                          instrumentNumericId={instrumentId}
                          instrumentTaskNumericId={task.id}
                          instrumentTaskLabel={String(task.name ?? '')}
                          lotId={lotId}
                          lotsGroupedByLot={lots}
                          defaultSelectedLotDetailId={currentDetailId}
                          allowsLotRetirementFromInstrumentPolicy={canRetireLot}
                        />,
                      )
                    }
                    return slots
                  })()
                : new Map<number, React.ReactNode>()

              // Typed lot row objects for TaskLogForm.
              const linkedLotRow: LotRow | null = hasLinkedLot
                ? ({
                    id: task.linkedLot!.id,
                    name: task.linkedLot!.name,
                    lotNumber: task.linkedLot!.lotNumber,
                    expirationDate: task.linkedLot!.expirationDate,
                    currentLotDetailId: task.currentLotDetailId,
                    lifecycleStatus: task.linkedLot!.lifecycleStatus,
                    isLotRetired: task.linkedLot!.isLotRetired,
                    isLotDiscarded: task.linkedLot!.isLotDiscarded,
                  } as LotRow)
                : null

              const usedLotRows: LotRow[] = usedLotsList.map(
                (ul) =>
                  ({
                    id: ul.id,
                    name: ul.name,
                    lotNumber: ul.lotNumber,
                    expirationDate: ul.expirationDate,
                    currentLotDetailId: ul.currentLotDetailId,
                    lifecycleStatus: ul.lifecycleStatus,
                    isLotRetired: ul.isLotRetired,
                    isLotDiscarded: ul.isLotDiscarded,
                  }) as LotRow,
              )

              const usedLotsWithoutSelection = usedLotsList.filter(
                (ul) => ul.currentLotDetailId == null,
              )

              const linkedLotMissingSelection = hasLinkedLot && task.currentLotDetailId == null

              const showViewDetails = canOpenTaskLog && task.lastLogId != null
              const showViewChart = hasChartableFields(task)
              const statusRowTint = taskStatusRowTint(task.taskStatusCode, task.latestQcPassed)

              return (
                <div key={task.id}>
                  {/* Row header — status tint mirrors legacy setPanelColor (REBUILD-470) */}
                  <div
                    className={cn(
                      'flex items-start gap-2 border p-3 min-w-0',
                      statusRowTint
                        ? taskStatusRowTintClassName(statusRowTint)
                        : 'border-transparent',
                    )}
                    data-status-tint={statusRowTint ?? undefined}
                  >
                    <button
                      type="button"
                      className="shrink-0 pt-0.5 text-muted-foreground hover:text-foreground"
                      onClick={() => toggleTask(task.id)}
                      aria-expanded={isOpen}
                      aria-label={task.name ?? undefined}
                    >
                      <ChevronDown
                        className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                      />
                    </button>

                    <div className="flex-1 min-w-0 space-y-1">
                      <button
                        type="button"
                        className="w-full text-left font-medium text-sm [overflow-wrap:anywhere] hover:text-foreground"
                        onClick={() => toggleTask(task.id)}
                      >
                        {task.name}
                        {/* REBUILD-123 UPD 19.06 / legacy: "| Tags:" after the task name */}
                        {task.tags.length > 0 && (
                          <span className="font-normal text-muted-foreground">
                            <span aria-hidden="true"> | </span>
                            {t('instruments.show.tasks.tagsLabel')}{' '}
                            <span className="font-medium text-[#5086d9]">
                              {task.tags.map((tag) => tag.name).join(', ')}
                            </span>
                          </span>
                        )}
                      </button>
                      <div className="flex flex-wrap items-center gap-2">
                        {task.taskStatusCode && (
                          <TaskStatusBadge taskStatusCode={task.taskStatusCode} prominent />
                        )}
                        {/* REBUILD-463: clear the ribbon once the QC failure's non-compliance
                            is resolved. hasUnresolvedQcFailure already folds in the NPT gate
                            (latest non-NPT QC fail) AND NC-review awareness, so it stays
                            consistent with the dashboard's open-NC clearing. */}
                        {task.hasUnresolvedQcFailure && <FailedNeedsRerunRibbon />}
                        {hasLinkedLot && (
                          <span className="text-xs text-muted-foreground">
                            {t('instruments.show.tasks.linkedTo')}{' '}
                            <span className="font-medium">{task.linkedLot!.name}</span>
                          </span>
                        )}
                      </div>
                      {(task.intervalTypeDescription || task.frequencyTypeCode) && (
                        <FrequencyContextPanel
                          frequencyTypeCode={task.frequencyTypeCode}
                          intervalTypeDescription={task.intervalTypeDescription}
                          frequencyRrule={task.frequencyRrule}
                          leadTimeIntervalHours={task.leadTimeIntervalHours}
                          leadTimeUnit={task.leadTimeUnit}
                          lateTimeIntervalHours={task.lateTimeIntervalHours}
                          lateTimeUnit={task.lateTimeUnit}
                          fixedScheduleStartTime={task.fixedScheduleStartTime}
                          endsAtMidnight={task.endsAtMidnight}
                        />
                      )}
                    </div>

                    {/* Row actions */}
                    {showActions && (
                      <div className="flex items-center gap-1 shrink-0">
                        {hasLinkedLot && canPersistLotSelection && (
                          <ControlsReagentsLotsModal
                            instrumentNumericId={instrumentId}
                            instrumentTaskNumericId={task.id}
                            instrumentTaskLabel={String(task.name ?? '')}
                            lotId={task.linkedLot!.id}
                            lotsGroupedByLot={lots}
                            defaultSelectedLotDetailId={task.currentLotDetailId}
                            allowsLotRetirementFromInstrumentPolicy={canRetireLot}
                          />
                        )}
                        {((canOpenTaskLog && taskHistoryEnabled) || canConfigureTaskDataFields) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Task History has no legacy equivalent and is hidden until the
                                  business defines it — gated on the shared flag (REBUILD-212). */}
                              {canOpenTaskLog && taskHistoryEnabled && (
                                <DropdownMenuItem asChild>
                                  <Link
                                    href={historyInstrumentTaskTaskLogPath(instrumentId, task.id)}
                                  >
                                    {t('instruments.show.tasks.taskHistory')}
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {canConfigureTaskDataFields && (
                                <DropdownMenuItem asChild>
                                  <Link href={instrumentTaskDataFieldsPath(instrumentId, task.id)}>
                                    {t('instruments.show.tasks.configureFields')}
                                  </Link>
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Lots missing lot detail selection banner */}
                  {(linkedLotMissingSelection || usedLotsWithoutSelection.length > 0) && (
                    <div className="mx-3 mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 space-y-2">
                      {linkedLotMissingSelection && (
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm text-amber-800">
                            {t('instruments.show.tasks.specifyLotBanner', {
                              lotName: supplyDisplayName(task.linkedLot!.name),
                            })}
                          </p>
                          {canPersistLotSelection && (
                            <ControlsReagentsLotsModal
                              instrumentNumericId={instrumentId}
                              instrumentTaskNumericId={task.id}
                              instrumentTaskLabel={String(task.name ?? '')}
                              lotId={task.linkedLot!.id}
                              lotsGroupedByLot={lots}
                              defaultSelectedLotDetailId={task.currentLotDetailId}
                              allowsLotRetirementFromInstrumentPolicy={canRetireLot}
                              triggerLabel={t('instruments.show.tasks.selectLotAction')}
                            />
                          )}
                        </div>
                      )}
                      {usedLotsWithoutSelection.map((lot) => (
                        <div key={lot.id} className="flex flex-wrap items-center gap-2">
                          <p className="text-sm text-amber-800">
                            {t('instruments.show.tasks.specifyLotBanner', {
                              lotName: supplyDisplayName(lot.name),
                            })}
                          </p>
                          {canPersistLotSelection && (
                            <ControlsReagentsLotsModal
                              instrumentNumericId={instrumentId}
                              instrumentTaskNumericId={task.id}
                              instrumentTaskLabel={String(task.name ?? '')}
                              lotId={lot.id}
                              lotsGroupedByLot={lots}
                              defaultSelectedLotDetailId={lot.currentLotDetailId}
                              allowsLotRetirementFromInstrumentPolicy={canRetireLot}
                              triggerLabel={t('instruments.show.tasks.selectLotAction')}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Expanded panel — timeline + read affordances + optional log form */}
                  {isOpen && (
                    <div className="px-4 pb-4 pt-2 border-t bg-muted/20 space-y-3">
                      {/* Compliance timeline */}
                      {task.lastPerformedAt && task.complianceWindow && (
                        <ComplianceTimeline
                          window={task.complianceWindow}
                          siteTimeZone={siteTimeZone}
                        />
                      )}

                      {/* Read affordances: View Last Performed + View Chart */}
                      {(showViewDetails || showViewChart) && (
                        <div className="flex flex-wrap items-center gap-2">
                          {showViewDetails && (
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setDetailsTaskLogId({ taskId: task.id, logId: task.lastLogId! })
                                }
                              >
                                <Info className="h-3.5 w-3.5 mr-1.5" />
                                {t('instruments.show.tasks.viewLastPerformed')}
                              </Button>
                              {task.lastPerformedAt && (
                                <span className="text-xs text-muted-foreground">
                                  {formatDateTime(task.lastPerformedAt, siteTimeZone)}
                                </span>
                              )}
                            </div>
                          )}
                          {showViewChart && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setChartTaskId(task.id)}
                            >
                              <BarChart2 className="h-3.5 w-3.5 mr-1.5" />
                              {t('instruments.show.tasks.viewChart')}
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Task notes — inside the log form for legacy ordering (after the
                          lots, before the data fields); standalone for read-only users,
                          who get no form at all but must still see the instructions. */}
                      {!canLogInline && <TaskNotesPanel notes={task.notes} />}

                      {/* Inline log form — only for users who can log */}
                      {canLogInline && (
                        <TaskLogForm
                          mode="new"
                          dataFields={task.dataFields}
                          loggerOptions={loggerOptions}
                          complianceWindow={task.complianceWindow}
                          existingTaskLog={null}
                          formAction={instrumentTaskTaskLogPath(instrumentId, task.id)}
                          formMethod="post"
                          errors={errors}
                          isInline
                          reloadOnly={['currentSchedule', 'instrument']}
                          onSuccess={handleInlineSuccess}
                          onCancel={() => setOpenTaskId(null)}
                          lotValidityWarnings={lotValidityWarnings}
                          linkedLot={linkedLotRow}
                          usedLots={usedLotRows}
                          lotModalSlots={lotModalSlots}
                          notesSlot={<TaskNotesPanel notes={task.notes} />}
                          logAuthenticationCode={logAuthenticationCode}
                          siteTimeZone={siteTimeZone}
                          instrumentId={instrumentId}
                          dayOff={task.dayOff}
                          scheduleLevel={task.scheduleLevel}
                        />
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </React.Fragment>
        ))}
      </div>

      {/* View Details dialog — reuses the Reports-tab LogDetailsDialog */}
      {detailsTaskLogId && (
        <LogDetailsDialog
          instrumentId={instrumentId}
          instrumentTaskId={detailsTaskLogId.taskId}
          instrumentLabel={instrumentLabel}
          serialNumber={null}
          taskLogId={detailsTaskLogId.logId}
          onClose={() => setDetailsTaskLogId(null)}
          // finishVoid passes deletedLogId; reload helper takes no args.
          onAfterDelete={() => {
            reloadInstrumentAndScheduleAfterVoid()
            onAfterTaskLog?.()
          }}
        />
      )}

      {/* View Chart dialog */}
      {chartTaskId != null && (
        <DataFieldChartDialog
          onClose={() => setChartTaskId(null)}
          instrumentId={instrumentId}
          instrumentTaskId={chartTaskId}
          taskName={tasks.find((task) => task.id === chartTaskId)?.name ?? null}
        />
      )}
    </div>
  )
}

export default InstrumentTasksAccordion
